/**
 * Puts an account id on every Sentry event, so an issue's "users affected" is a real number.
 *
 * **What this is for.** Until now `Sentry.setUser` was never called anywhere in orion-web, so every
 * issue in the project reads `0 users` — and volume is then the only signal there is. That is the
 * difference between "one tab in a reconnect loop" and "everyone on /routines", and those two call
 * for opposite responses. It is also the number that decides whether an issue is worth a deploy:
 * 1900 events from 3 accounts is a bug in an edge case, 1900 from 900 accounts is an outage.
 *
 * **Why the metasite and nothing else.** The id is the Orion metasite — the same opaque tenant id
 * the rest of the product keys on, already in the browser as a claim inside the signed instance we
 * mint for duplexer (lib/orion-instance.ts). So this costs no request, and carries no name, email
 * or phone number into Sentry. `username`/`email`/`ip_address` are deliberately NOT set: a count
 * per account is the whole ask, and anything more would put PII in a third-party store to answer a
 * question nobody has.
 *
 * **Where it is called from.** Every place the browser comes to hold an instance:
 * lib/duplexer-connection.ts's mint (every signed-in page that subscribes to a channel, which is
 * where the noisy issues live), and both halves of lib/instance-auth.ts — the acquire AND the
 * cached read, because a reload rehydrates its token from localStorage and never re-acquires.
 *
 * The cached read is the only synchronous one. Everywhere else this is on an async path, so the
 * very first events of a cold page load — a render throw during hydration — can still arrive
 * unidentified. That is the honest cost of not adding a request just for this.
 *
 * **What it does not close.** A session that changes WITHOUT a sign-out — re-auth as a different
 * account while this tab's `localStorage` token is still inside its expiry window — leaves
 * instance-auth serving the previous account's instance, and this labels what that token says.
 * That is a pre-existing hazard of the cached token itself (the app sends it as `Authorization`
 * too), not of the label; closing it means binding the cached instance to the session, which is a
 * change to instance-auth, not to this file.
 */

import * as Sentry from '@sentry/nextjs';

import { decodeInstancePayload } from './wix-instance-payload';

/** The metasite claim inside a signed instance, or null when it carries none. */
export function metaSiteIdFromInstance(instance: string): string | null {
  const metaSiteId = decodeInstancePayload(instance)?.metaSiteId;
  return typeof metaSiteId === 'string' && metaSiteId !== '' ? metaSiteId : null;
}

/**
 * The instance string this last looked at, so a repeat call costs a string compare.
 *
 * Load-bearing, not an optimisation: it is what lets callers hand over a token on the READ path —
 * `instance-auth`'s `fresh()` runs on every `getStoredInstance()` — instead of only where one
 * freshly arrives. Dedupe on the resolved id alone would still `atob` + `JSON.parse` every call.
 */
let lastInstance: string | null = null;
/** The id currently on the scope, so re-minting a NEW token for the same account does no work. */
let identified: string | null = null;

/**
 * Label this browser's Sentry scope with the account the instance was minted for. Cheap to call
 * repeatedly with the same token, and a no-op for an instance with no metasite claim — the
 * previous id is then LEFT in place rather than cleared, because an unreadable instance is not
 * evidence that the tab changed accounts.
 *
 * Safe to call with a token out of a client-side cache as well as a freshly fetched one, and that
 * rests on ONE invariant held by the callers: whatever clears the id clears the token beside it.
 * `clearInstance()` does both, so there is no state in which a cache can hand back an id that was
 * deliberately dropped — see the note in `duplexer-connection.ts`'s `retireOnRefusal`, which
 * declines to clear the id for exactly this reason.
 */
export function identifyFromInstance(instance: string): void {
  if (instance === lastInstance) return;
  lastInstance = instance;
  const metaSiteId = metaSiteIdFromInstance(instance);
  if (!metaSiteId || metaSiteId === identified) return;
  identified = metaSiteId;
  Sentry.setUser({ id: metaSiteId });
}

/**
 * Drop the label. Called only from `clearInstance()`, which drops the stored token in the same
 * breath — the pairing the invariant above depends on.
 *
 * Forgets the token too, so a caller that goes on serving the SAME instance afterwards
 * re-identifies rather than staying anonymous for the rest of the page's life.
 */
export function clearSentryIdentity(): void {
  lastInstance = null;
  identified = null;
  Sentry.setUser(null);
}
